import React, { Component } from 'react';
import { array, arrayOf, bool, func, object, oneOf, shape, string } from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import classNames from 'classnames';

import { useIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  mergeSearchFormKeywords,
} from '../../util/search';
import { parse } from '../../util/urlHelpers';
import { propTypes } from '../../util/types';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { resolveDiscoveryListingForCard } from '../../util/resolveDiscoveryListingForCard';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { H3, H5, LayoutSingleColumn, ModalInMobile, Page } from '../../components';
import TopbarSearchForm from '../../components/Topbar/TopbarSearchForm/TopbarSearchForm';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';

import { setActiveListing, isDiscoveryPrimaryEventsEnabled } from './SearchPage.duck';
import { primaryEventsToMapListings } from '../../util/primaryEventMapListing';
import {
  filterPrimaryEventsByBounds,
  filterPrimaryEventsByDiscoveryFilters,
  filterPrimaryEventsByKeywords,
} from '../../util/discoveryPrimaryEventsPanel';
import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  clearedSearchParamsWithoutFiltersMapAndGeo,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import SearchMap from './SearchMap/SearchMap';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersSecondary from './SearchFiltersSecondary/SearchFiltersSecondary';
import SearchFiltersPrimary from './SearchFiltersPrimary/SearchFiltersPrimary';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import css from './SearchPage.module.css';
import SearchPageHeroCarousel from './SearchPageHeroCarousel';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout
const SEARCH_WITH_MAP_DEBOUNCE = 300; // Little bit of debounce before search is initiated.
const HOMEPAGE_META_TITLE = 'Find Event Tickets Worldwide | Concerts, Sports & More | PromoTix';
const HOMEPAGE_META_DESCRIPTION =
  'Discover events and buy tickets to concerts, sports, festivals, and attractions around the world. Experience more with PromoTix.';

// Primary filters have their content in dropdown-popup.
// With this offset we move the dropdown to the left a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isSearchMapOpenOnMobile: false,
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      isSecondaryFiltersOpen: false,
      cachedPrimaryMapPins: null,
      mapSearchMinHeight: null,
    };

    this.resultsContainer = React.createRef();
    this.mapContainer = React.createRef();

    this.onMapMoveEnd = debounce(this.onMapMoveEnd.bind(this), SEARCH_WITH_MAP_DEBOUNCE);
    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);

    // Filter functions
    this.applyFilters = this.applyFilters.bind(this);
    this.cancelFilters = this.cancelFilters.bind(this);
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);
    this.handlePersistedSearchSubmit = this.handlePersistedSearchSubmit.bind(this);
    this.clearAllFiltersAndMapBounds = this.clearAllFiltersAndMapBounds.bind(this);
    this.updateCachedPrimaryMapPins = this.updateCachedPrimaryMapPins.bind(this);
  }

  componentDidMount() {
    if (!this.props.searchInProgress && isDiscoveryPrimaryEventsEnabled) {
      this.updateCachedPrimaryMapPins();
    }
  }

  componentDidUpdate(prevProps) {
    const { searchInProgress, location } = this.props;
    if (
      prevProps.location !== location &&
      !location.state?.preserveScroll &&
      this.state.mapSearchMinHeight !== null
    ) {
      this.setState({ mapSearchMinHeight: null });
    }
    if (
      prevProps.searchInProgress &&
      !searchInProgress &&
      isDiscoveryPrimaryEventsEnabled
    ) {
      this.updateCachedPrimaryMapPins();
    }
  }

  updateCachedPrimaryMapPins() {
    const { primaryEvents, location, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const { searchParamsInURL } = searchParamsPicker(
      location.search,
      this.props.searchParams,
      listingFieldsConfig,
      defaultFiltersConfig,
      sortConfig,
      isOriginInUse(config)
    );
    const validQueryParams = validFilterParams(
      searchParamsInURL,
      listingFieldsConfig,
      defaultFiltersConfig,
      false
    );
    const selectedFilters = validFilterParams(
      validQueryParams,
      listingFieldsConfig,
      defaultFiltersConfig
    );
    const parsedSearch = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });
    const keywordsFromUrl = parsedSearch.keywords;
    const marketplaceCurrency = config.currency;
    const filtered = filterPrimaryEventsByKeywords(
      filterPrimaryEventsByDiscoveryFilters(primaryEvents || [], selectedFilters),
      keywordsFromUrl
    );
    const pins = primaryEventsToMapListings(filtered, marketplaceCurrency);
    this.setState({ cachedPrimaryMapPins: pins });
  }

  // Callback to determine if new search is needed
  // when map is moved by user or viewport has changed
  onMapMoveEnd(viewportBoundsChanged, data) {
    const { viewportBounds, viewportMapCenter } = data;

    const routes = this.props.routeConfiguration;
    const searchPagePath = pathByRouteName('SearchPage', routes);
    const currentPath =
      typeof window !== 'undefined' && window.location && window.location.pathname;

    // When using the ReusableMapContainer onMapMoveEnd can fire from other pages than SearchPage too
    const isSearchPage = currentPath === searchPagePath;

    // If mapSearch url param is given
    // or original location search is rendered once,
    // we start to react to "mapmoveend" events by generating new searches
    // (i.e. 'moveend' event in Mapbox and 'bounds_changed' in Google Maps)
    if (viewportBoundsChanged && isSearchPage) {
      const { history, location, config } = this.props;
      const { listingFields: listingFieldsConfig } = config?.listing || {};
      const { defaultFilters: defaultFiltersConfig } = config?.search || {};

      // parse query parameters, including a custom attribute named category
      const { address, bounds, mapSearch, origin, ...rest } = parse(location.search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
      });

      const originMaybe = isOriginInUse(this.props.config) ? { origin: viewportMapCenter } : {};
      const dropNonFilterParams = false;

      const searchParams = {
        address,
        ...originMaybe,
        bounds: viewportBounds,
        mapSearch: true,
        ...validFilterParams(rest, listingFieldsConfig, defaultFiltersConfig, dropNonFilterParams),
      };

      // Refresh the events in view without treating a pan/zoom as a new page visit.
      // A shorter result list must not pull the sticky map upward or clamp page scroll.
      // Reserve only the space needed for its current position, not the whole old list.
      const resultsContainer = this.resultsContainer?.current;
      const mapContainer = this.mapContainer?.current;
      if (window.innerWidth > MODAL_BREAKPOINT && resultsContainer && mapContainer) {
        const mapSearchMinHeight = Math.ceil(
          mapContainer.getBoundingClientRect().bottom - resultsContainer.getBoundingClientRect().top
        );
        this.setState({ mapSearchMinHeight });
      }
      history.push(createResourceLocatorString('SearchPage', routes, {}, searchParams), {
        preserveScroll: true,
      });
    }
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  // Apply the filters by redirecting to SearchPage with new filters.
  applyFilters() {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const searchParams = { ...urlQueryParams, ...this.state.currentQueryParams };
    const search = cleanSearchFromConflictingParams(
      searchParams,
      listingFieldsConfig,
      defaultFiltersConfig,
      sortConfig
    );

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, search));
  }

  // Close the filters by clicking cancel, revert to the initial params
  cancelFilters() {
    this.setState({ currentQueryParams: {} });
  }

  // Reset filters modal: same full reset as Clear filters (geo, keywords, bounds, all filters).
  resetAll(e) {
    const { history, routeConfiguration, config, location } = this.props;
    const cleared = clearedSearchParamsWithoutFiltersMapAndGeo(location.search, config);
    this.setState({ currentQueryParams: {}, isSecondaryFiltersOpen: false });
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, { ...cleared, page: 1 }));
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  /** Same behavior as Topbar mobile search — merge into current URL and go to SearchPage. */
  handlePersistedSearchSubmit(values) {
    const { history, routeConfiguration, config, location, searchParams } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const { urlQueryParams } = searchParamsPicker(
      location.search,
      searchParams,
      listingFieldsConfig,
      defaultFiltersConfig,
      sortConfig,
      isOriginInUse(config)
    );

    const geoKeys = [
      'address',
      'bounds',
      ...(isOriginInUse(config) ? ['origin'] : []),
    ];

    let nextSearch;
    if (isMainSearchTypeKeywords(config)) {
      nextSearch = mergeSearchFormKeywords(
        {
          ...urlQueryParams,
          keywords: values?.keywords,
          page: 1,
        },
        values
      );
    } else {
      const { search, selectedPlace } = values?.location || {};
      const trimmedSearch = search != null ? String(search).trim() : '';
      if (!trimmedSearch) {
        nextSearch = mergeSearchFormKeywords({ ...omit(urlQueryParams, geoKeys), page: 1 }, values);
      } else {
        const { origin, bounds } = selectedPlace || {};
        const originMaybe = isOriginInUse(config) && origin ? { origin } : {};
        nextSearch = mergeSearchFormKeywords(
          {
            ...urlQueryParams,
            ...originMaybe,
            address: search,
            bounds,
            page: 1,
          },
          values
        );
      }
    }
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, nextSearch));
  }

  /** Clears listing filters, keyword/location search, and map bounds. */
  clearAllFiltersAndMapBounds() {
    const { history, routeConfiguration, config, location } = this.props;
    const cleared = clearedSearchParamsWithoutFiltersMapAndGeo(location.search, config);
    this.setState({ currentQueryParams: {}, isSecondaryFiltersOpen: false });
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, { ...cleared, page: 1 }));
  }

  getHandleChangedValueFn(useHistoryPush) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };

        // Address and bounds are handled outside of MainPanel.
        // I.e. TopbarSearchForm && search by moving the map.
        // We should always trust urlQueryParams with those.
        // Keywords live in the URL even when mainSearch is `location` (dual search bar).
        const keywordsMaybe = isMainSearchTypeKeywords(config) || keywords ? { keywords } : {};
        return {
          currentQueryParams: {
            ...mergedQueryParams,
            ...updatedURLParams,
            ...keywordsMaybe,
            address,
            bounds,
          },
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(
            searchParams,
            listingFieldsConfig,
            defaultFiltersConfig,
            sortConfig
          );
          history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, search));
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
  }

  render() {
    const {
      intl,
      listings,
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams,
      activeListingId,
      primaryEvents,
      primaryEventsInProgress,
      primaryEventsError,
      mergedDiscoveryItems,
      onActivateListing,
      routeConfiguration,
      config,
    } = this.props;

    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};

    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const marketplaceCurrency = config.currency;

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      listingFieldsConfig,
      defaultFiltersConfig,
      sortConfig,
      isOriginInUse(config)
    );

    const { bounds: urlBounds } = searchParamsInURL || {};

    const validQueryParams = validFilterParams(
      searchParamsInURL,
      listingFieldsConfig,
      defaultFiltersConfig,
      false
    );

    const isWindowDefined = typeof window !== 'undefined';
    const isMobileLayout = isWindowDefined && window.innerWidth < MODAL_BREAKPOINT;
    const shouldShowSearchMap =
      !isMobileLayout || (isMobileLayout && this.state.isSearchMapOpenOnMobile);

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const parsedSearchForTopbarForm = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });
    const {
      keywords: keywordsFromUrl,
      address: addressFromUrl,
      origin: originFromUrl,
      bounds: boundsFromUrl,
    } = parsedSearchForTopbarForm;
    const topbarSearchInitialValues = isKeywordSearch
      ? { keywords: keywordsFromUrl || '' }
      : (() => {
          const locationFieldsPresent = isOriginInUse(config)
            ? addressFromUrl && originFromUrl && boundsFromUrl
            : addressFromUrl && boundsFromUrl;
          return {
            keywords: keywordsFromUrl || '',
            location: locationFieldsPresent
              ? {
                  search: addressFromUrl,
                  selectedPlace: {
                    address: addressFromUrl,
                    origin: originFromUrl,
                    bounds: boundsFromUrl,
                  },
                }
              : { search: '', selectedPlace: null },
          };
        })();
    const defaultFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(f => f.key !== 'keywords')
      : defaultFiltersConfig;
    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );
    const availablePrimaryFilters = [...defaultFilters, ...customPrimaryFilters];
    const availableFilters = [
      ...defaultFilters,
      ...customPrimaryFilters,
      ...customSecondaryFilters,
    ];

    const hasSecondaryFilters = !!(customSecondaryFilters && customSecondaryFilters.length > 0);

    // Selected aka active filters
    const selectedFilters = validFilterParams(
      validQueryParams,
      listingFieldsConfig,
      defaultFiltersConfig
    );

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      listingFieldsConfig,
      defaultFiltersConfig
    );
    const cleanedForPrimarySort = cleanSearchFromConflictingParams(
      validQueryParams,
      listingFieldsConfig,
      defaultFiltersConfig,
      sortConfig
    );
    const primaryEventsDiscoveryFiltered = isDiscoveryPrimaryEventsEnabled
      ? filterPrimaryEventsByKeywords(
          filterPrimaryEventsByDiscoveryFilters(primaryEvents || [], selectedFilters),
          keywordsFromUrl
        )
      : [];
    const primaryMapPins =
      isDiscoveryPrimaryEventsEnabled && !searchInProgress
        ? primaryEventsToMapListings(primaryEventsDiscoveryFiltered, marketplaceCurrency)
        : isDiscoveryPrimaryEventsEnabled
          ? this.state.cachedPrimaryMapPins || []
          : [];
    const mapListings = [...(listings || []), ...primaryMapPins];
    const keysOfSelectedFilters = Object.keys(selectedFilters);
    const selectedFiltersCountForMobile = isKeywordSearch
      ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
      : keysOfSelectedFilters.length;
    const isValidEventDatesFilter =
      searchParamsInURL.eventDates == null ||
      (searchParamsInURL.eventDates != null &&
        searchParamsInURL.eventDates === selectedFilters.eventDates);

    // Selected aka active secondary filters
    const selectedSecondaryFilters = hasSecondaryFilters
      ? validFilterParams(validQueryParams, customSecondaryFilters, [])
      : {};
    const selectedSecondaryFiltersCount = Object.keys(selectedSecondaryFilters).length;

    const isSecondaryFiltersOpen = !!hasSecondaryFilters && this.state.isSecondaryFiltersOpen;
    const propsForSecondaryFiltersToggle = hasSecondaryFilters
      ? {
        isSecondaryFiltersOpen: this.state.isSecondaryFiltersOpen,
        toggleSecondaryFiltersOpen: isOpen => {
          this.setState({ isSecondaryFiltersOpen: isOpen, currentQueryParams: {} });
        },
        selectedSecondaryFiltersCount,
      }
      : {};

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
          ? listings.length
          : 0;
    const listingsAreLoaded =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported) &&
      !(isDiscoveryPrimaryEventsEnabled && primaryEventsInProgress);

    const combinedResultsCount = totalItems;
    const combinedCountsReady =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported) &&
      (!isDiscoveryPrimaryEventsEnabled || !primaryEventsInProgress);
    const countLineLoading = !combinedCountsReady && !searchListingsError;

    const discoveryItemsForPanel = isDiscoveryPrimaryEventsEnabled
      ? mergedDiscoveryItems
      : null;
    const paginationForResultsPanel =
      pagination && listingsAreLoaded ? pagination : null;
    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          searchParams={searchParamsInURL}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        primaryEventsCount={0}
        location={location}
        resetAll={this.clearAllFiltersAndMapBounds}
      />
    );

    const { bounds, origin } = searchParamsInURL || {};

    const { title, description, schema } = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config
    );

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={HOMEPAGE_META_DESCRIPTION}
        title={HOMEPAGE_META_TITLE}
        schema={schema}
      >
        <LayoutSingleColumn
        // mainColumnClassName={css.layoutWrapperMain}
        topbar={
          <TopbarContainer
            className={topbarClasses}
            currentPage="SearchPage"
            currentSearchParams={urlQueryParams}
            mobileLogoCentered={this.state.isSearchMapOpenOnMobile}
          />
        }
        // footer={<FooterContainer />}
      >
        <div>
          {!this.state.isSearchMapOpenOnMobile ? <SearchPageHeroCarousel /> : null}
          <div className={css.container}>
          <div
            className={css.listingWrapper}
            ref={this.resultsContainer}
            style={
              !isMobileLayout && this.state.mapSearchMinHeight !== null
                ? {
                    minHeight: this.state.mapSearchMinHeight,
                    // Do not anchor page scroll to event cards replaced by a map search.
                    overflowAnchor: 'none',
                  }
                : undefined
            }
          >
            <div className={css.searchResultContainer}>
              <SearchFiltersMobile
                className={css.searchFiltersMobileMap}
                urlQueryParams={validQueryParams}
                sortByComponent={sortBy('mobile')}
                listingsAreLoaded={listingsAreLoaded}
                resultsSummaryReady={combinedCountsReady}
                resultsCount={combinedResultsCount}
                searchInProgress={countLineLoading}
                searchListingsError={searchListingsError}
                showAsModalMaxWidth={MODAL_BREAKPOINT}
                onMapIconClick={() => {
                  this.setState({ isSearchMapOpenOnMobile: true });
                }}
                onManageDisableScrolling={onManageDisableScrolling}
                onOpenModal={this.onOpenMobileModal}
                onCloseModal={this.onCloseMobileModal}
                resetAll={this.clearAllFiltersAndMapBounds}
                selectedFiltersCount={selectedFiltersCountForMobile}
                noResultsInfo={noResultsInfo}
                isMapVariant
                /* Always rendered (not gated on window/innerWidth): the whole
                   SearchFiltersMobile container is display:none on desktop, and gating on
                   `isMobileLayout` meant SSR HTML omitted the form — it popped in under
                   the summary bar on mobile hydration (CLS). */
                persistentSearch={
                  <div className={css.persistSearchFormWrap}>
                    <TopbarSearchForm
                      rootClassName={css.persistSearchFormRoot}
                      onSubmit={this.handlePersistedSearchSubmit}
                      initialValues={topbarSearchInitialValues}
                      isMobile
                      stackedLayout
                      appConfig={config}
                    />
                  </div>
                }
              >
                {availableFilters.map(config => {
                  return (
                    <FilterComponent
                      key={`SearchFiltersMobile.${config.key}`}
                      idPrefix="SearchFiltersMobile"
                      config={config}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={validQueryParams}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      liveEdit
                      showAsPopup={false}
                    />
                  );
                })}
              </SearchFiltersMobile>
              <MainPanelHeader
                className={css.mainPanelMapVariant}
                sortByComponent={sortBy('desktop')}
                isSortByActive={sortConfig.active}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={combinedResultsCount}
                searchInProgress={countLineLoading}
                searchListingsError={searchListingsError}
                noResultsInfo={noResultsInfo}
              >
                <SearchFiltersPrimary {...propsForSecondaryFiltersToggle}>
                  {availablePrimaryFilters.map(config => {
                    return (
                      <FilterComponent
                        key={`SearchFiltersPrimary.${config.key}`}
                        idPrefix="SearchFiltersPrimary"
                        config={config}
                        marketplaceCurrency={marketplaceCurrency}
                        urlQueryParams={validQueryParams}
                        initialValues={initialValues(this.props, this.state.currentQueryParams)}
                        getHandleChangedValueFn={this.getHandleChangedValueFn}
                        intl={intl}
                        showAsPopup
                        contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
                      />
                    );
                  })}
                  <button
                    type="button"
                    className={css.filterPillButton}
                    onClick={this.clearAllFiltersAndMapBounds}
                  >
                    {intl.formatMessage({ id: 'SearchPage.clearFilters' })}
                  </button>
                </SearchFiltersPrimary>
              </MainPanelHeader>
              {isSecondaryFiltersOpen ? (
                <div className={classNames(css.searchFiltersPanel)}>
                  <SearchFiltersSecondary
                    urlQueryParams={validQueryParams}
                    listingsAreLoaded={listingsAreLoaded}
                    applyFilters={this.applyFilters}
                    cancelFilters={this.cancelFilters}
                    resetAll={this.resetAll}
                    onClosePanel={() => this.setState({ isSecondaryFiltersOpen: false })}
                  >
                    {customSecondaryFilters.map(config => {
                      return (
                        <FilterComponent
                          key={`SearchFiltersSecondary.${config.key}`}
                          idPrefix="SearchFiltersSecondary"
                          config={config}
                          marketplaceCurrency={marketplaceCurrency}
                          urlQueryParams={validQueryParams}
                          initialValues={initialValues(this.props, this.state.currentQueryParams)}
                          getHandleChangedValueFn={this.getHandleChangedValueFn}
                          intl={intl}
                          showAsPopup={false}
                        />
                      );
                    })}
                  </SearchFiltersSecondary>
                </div>
              ) : (
                <div
                  className={classNames(css.listingsForMapVariant, {
                    [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                  })}
                >
                  {searchListingsError ? (
                    <H3 className={css.error}>
                      <FormattedMessage id="SearchPage.searchError" />
                    </H3>
                  ) : null}
                  {isDiscoveryPrimaryEventsEnabled && primaryEventsError ? (
                    <H5 className={css.primaryEventsError}>
                      <FormattedMessage id="SearchPage.primaryEventsError" />
                    </H5>
                  ) : null}
                  {!isValidEventDatesFilter ? (
                    <H5>
                      <FormattedMessage id="SearchPage.invalidDatesFilter" />
                    </H5>
                  ) : null}
                  <SearchResultsPanel
                    className={css.searchListingsPanel}
                    listings={listings}
                    discoveryItems={discoveryItemsForPanel}
                    pagination={paginationForResultsPanel}
                    search={parse(location.search)}
                    setActiveListing={onActivateListing}
                    isMapVariant
                  />
                </div>
              )}
            </div>
            <ModalInMobile
              className={classNames(css.mapPanel, {
                [css.mapPanelInsetBelowTopbar]:
                  isMobileLayout && this.state.isSearchMapOpenOnMobile,
              })}
              id="SearchPage.map"
              isModalOpenOnMobile={this.state.isSearchMapOpenOnMobile}
              onClose={() => {
                this.setState({ isSearchMapOpenOnMobile: false });
              }}
              showAsModalMaxWidth={MODAL_BREAKPOINT}
              onManageDisableScrolling={onManageDisableScrolling}
              closeButtonMessage={<FormattedMessage id="SearchPage.closeMap" />}
            >
              <div className={css.mapWrapper} data-testid="searchMapContainer" ref={this.mapContainer}>
                {shouldShowSearchMap ? (
                  <SearchMap
                    reusableContainerClassName={css.map}
                    activeListingId={activeListingId}
                    bounds={bounds}
                    center={origin}
                    isSearchMapOpenOnMobile={this.state.isSearchMapOpenOnMobile}
                    location={location}
                    listings={mapListings}
                    onMapMoveEnd={this.onMapMoveEnd}
                    onCloseAsModal={() => {
                      onManageDisableScrolling('SearchPage.map', false);
                    }}
                    messages={intl.messages}
                  />
                ) : null}
              </div>
            </ModalInMobile>
          </div>
        </div>
          </div>
          <FooterContainer />
        </LayoutSingleColumn>
      </Page>
    );
  }
}

SearchPageComponent.defaultProps = {
  listings: [],
  primaryEvents: [],
  primaryEventsInProgress: false,
  primaryEventsError: null,
  mergedDiscoveryItems: null,
  pagination: null,
  searchListingsError: null,
  searchParams: {},
  activeListingId: null,
};

SearchPageComponent.propTypes = {
  listings: array,
  primaryEvents: array,
  primaryEventsInProgress: bool,
  mergedDiscoveryItems: array,
  primaryEventsError: propTypes.error,
  onActivateListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  pagination: propTypes.pagination,
  scrollingDisabled: bool.isRequired,
  searchInProgress: bool.isRequired,
  searchListingsError: propTypes.error,
  searchParams: object,

  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    activeListingId,
    primaryEvents,
    primaryEventsInProgress,
    primaryEventsError,
    mergedDiscoveryItems,
  } = state.SearchPage;

  // Wrap denormalisation: if any listing references an entity that isn't in the
  // Redux store, `denormalisedEntities` throws — and a throw inside mapStateToProps
  // makes React-Redux unmount/remount loadable children on every store update,
  // producing the 26k-error render loop. Falling back to the wire listing keeps
  // the page renderable.
  const safeGetListing = id => {
    try {
      return getListingsById(state, [id])[0] || null;
    } catch (e) {
      return null;
    }
  };

  let listings = [];
  try {
    listings = getListingsById(state, currentPageResultIds);
  } catch (e) {
    listings = [];
  }

  const resolvedDiscoveryItems =
    mergedDiscoveryItems && mergedDiscoveryItems.length
      ? mergedDiscoveryItems.map(item => {
          if (item.kind !== 'listing' || !item.listing?.id) {
            return item;
          }
          const listing = resolveDiscoveryListingForCard(state, item.listing, (st, ids) => {
            try {
              return getListingsById(st, ids);
            } catch (e) {
              return [];
            }
          });
          return { kind: 'listing', listing };
        })
      : mergedDiscoveryItems;

  return {
    listings,
    primaryEvents,
    primaryEventsInProgress,
    primaryEventsError,
    mergedDiscoveryItems: resolvedDiscoveryItems,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
    activeListingId,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onActivateListing: listingId => dispatch(setActiveListing(listingId)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedSearchPage);

export default SearchPage;
